import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { fetchTranslators } from '../../../store/thunks/translatorsThunk';
import { Translator } from '../../../types';
import ClientMenu from '../ClientMenu/ClientMenu';
import ConfirmDeleteModal from '../ConfirmDeleteModal/ConfirmDeleteModal';
import TranslatorInfoModal from '../TranslatorInfoModal/TranslatorInfoModal';
import WorkSchedule from '../WorkSchedule/WorkSchedule';
import WorkScheduleForm from '../WorkScheduleForm/WorkScheduleForm';
import { useMenuContext } from '../contexts/MenuContext';
import { avatarColor, initialOf } from '../../common/avatar';
import MoreVert from '../../Customer/icons/MoreVert';
import style from './ClientItem.module.css';
import { adjustMenuPosition, handleDeleteClick } from './helpers';

interface ClientItemProps {
  translator: Translator;
  index: number;
  onDelete: (translatorId: string) => void;
  onRefresh: () => void;
}

const ClientItem: React.FC<ClientItemProps> = ({
  translator,
  index,
  onDelete,
  onRefresh,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    menuOpen,
    menuPosition,
    toggleMenu,
    onDetailsClick,
    onSendInvitationClick,
    setMenuOpen,
  } = useMenuContext();

  const menuRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [adjustedMenuPosition, setAdjustedMenuPosition] =
    useState(menuPosition);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef, setMenuOpen]);

  useEffect(() => {
    if (menuOpen === translator.id && menuRef.current) {
      setAdjustedMenuPosition(adjustMenuPosition(menuRef, menuPosition));
    }
  }, [menuOpen, menuPosition, translator.id]);

  const handleWorkScheduleClick = () => {
    setIsInfoModalOpen(false);
    setMenuOpen(null);
    setTimeout(() => setIsModalOpen(true));
  };

  const handleInfoClick = () => {
    setIsInfoModalOpen(true);
    setMenuOpen(null);
  };

  const handleSaveSchedule = (
    id: string,
    schedule: Translator['weeklyWorkSchedule'],
  ) => setIsModalOpen(false);

  const handleDeleteClickWithConfirmation = () => {
    setMenuOpen(null);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    await handleDeleteClick(translator.id, onDelete);
    setIsDeleteModalOpen(false);
    dispatch(fetchTranslators(0, 18, 'ASC'));
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <div
      className={style.client}
      role="button"
      tabIndex={0}
      onClick={() => setIsInfoModalOpen(true)}
    >
      <div className={style.rowNum}>{index + 1}</div>
      <div className={style.clientInfo}>
        {translator.avatarUrl ? (
          <img
            src={translator.avatarUrl}
            alt={`${translator.userName}'s avatar`}
            className={style.avatar}
          />
        ) : (
          <span
            className={style.avatarInitial}
            style={{ backgroundColor: avatarColor(translator.userName || '') }}
          >
            {initialOf(translator.userName || '')}
          </span>
        )}
        <div className={style.clientText}>
          <div className={style.clientName}>{translator.userName}</div>
          <a
            className={style.clientPhone}
            href={`tel:${translator.phone}`}
            onClick={(e) => e.stopPropagation()}
          >
            {translator.phone}
          </a>
        </div>
      </div>
      <div className={style.clientDays}>
        <WorkSchedule schedule={translator.weeklyWorkSchedule} />
      </div>
      <div className={style.clientActions}>
        <button
          className={style.actionButton}
          onClick={(event) => {
            event.stopPropagation();
            toggleMenu(translator.id, event);
          }}
        >
          <MoreVert />
        </button>
        {menuOpen === translator.id &&
          ReactDOM.createPortal(
            <div
              ref={menuRef}
              className={style.menuContainer}
              style={{
                position: 'absolute',
                top: adjustedMenuPosition.top,
                left: adjustedMenuPosition.left,
              }}
            >
              <ClientMenu
                onWorkScheduleClick={handleWorkScheduleClick}
                onDetailsClick={(e) => {
                  onDetailsClick(translator.id, e);
                  handleInfoClick();
                }}
                onSendInvitationClick={(e) =>
                  onSendInvitationClick(translator.id, e)
                }
                onDeleteClick={handleDeleteClickWithConfirmation}
              />
            </div>,
            document.body,
          )}
      </div>
      {isModalOpen &&
        ReactDOM.createPortal(
          <div onClick={(e) => e.stopPropagation()}>
            <WorkScheduleForm
              translatorId={translator.id}
              translatorName={translator.userName}
              onSave={handleSaveSchedule}
              onClose={() => setIsModalOpen(false)}
              onRefresh={onRefresh}
            />
          </div>,
          document.body,
        )}
      {isDeleteModalOpen &&
        ReactDOM.createPortal(
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmDeleteModal
              userName={translator.userName}
              avatarUrl={translator.avatarUrl}
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
            />
          </div>,
          document.body,
        )}
      {isInfoModalOpen &&
        ReactDOM.createPortal(
          <div onClick={(e) => e.stopPropagation()}>
            <TranslatorInfoModal
              onClose={() => setIsInfoModalOpen(false)}
              translator={translator}
              onRefresh={onRefresh}
              onOpenWorkSchedule={handleWorkScheduleClick}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ClientItem;
